import { Injectable } from '@angular/core';
import  {HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, publishReplay, refCount } from 'rxjs/operators';
import { Observable, ReplaySubject } from 'rxjs';

import { AppMonitoringService } from '@core/services/app-monitoring.service';
import { MonitoringAttributes } from '@core/enums/monitoring-attributes.enum';
import { StorageService } from '@core/services/storage.service';

import { CustomerModel } from '../models/customer.model';
import { CustomerConverter } from '../converters/customer.converter';
import { Customer } from '../DTO/customer';
import { AccountModel } from '../models/account.model';
import { AccountConverter } from '../converters/account.converter';
import { Account } from '../DTO/account';
import { DomainModel } from '../models/domain.model';
import { Domain } from '../DTO/domain';
import { DomainConverter } from '../converters/domain.converter';
import { MessageType } from '../constants/message-type.constant';
import { MessageService } from './message.service';
import { ProductState } from '../DTO/productState';
import { ConfigurationService } from './configuration.service';


@Injectable({
  providedIn: 'root'
})
export class ClientAccountService {

  public cachedClients: Observable<any>;
  private client: any;
  private account: any;
  private customerToChange: any;
  private accountToChange: any;
  private customerChosen = new ReplaySubject<boolean>(1);
  public onCustomerChoice = this.customerChosen.asObservable();
  private accountChosen = new ReplaySubject<boolean>(1);
  public onAccountChoice = this.accountChosen.asObservable();

  constructor(private httpClient: HttpClient,
              public storageService: StorageService,
              private customerConverter: CustomerConverter,
              private accountConverter: AccountConverter,
              private domainConverter: DomainConverter,
              private messageService: MessageService,
              private router: Router,
              private configurationService: ConfigurationService,
              private monitoringService: AppMonitoringService,
  ) {
    this.client = null;
    this.account = null;
    this.accountChosen.next(false);
    this.customerChosen.next(false);
  }

  private getBaseResourceEndpoint(): string {
    return (
      this.configurationService.configuration.api.client
    );
  }

  getClients(): Observable<Array<CustomerModel>> {
    if (!this.cachedClients) {
      this.cachedClients = this.httpClient.get(this.getBaseResourceEndpoint()).pipe(
        map((res: Array<Customer>) => this.customerConverter.convertToModelList(res)),
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount(),
        // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      );
    }
    return this.cachedClients;
  }

  getAccounts(customerCode: any): Observable<Array<AccountModel>> {
    return this.httpClient.get(this.getBaseResourceEndpoint() + `/${customerCode}/accounts`)
      .pipe(
        map((res: Array<Account>) => this.accountConverter.convertToModelList(res))
      );
  }

  getDomanis(customerCode: any, accountCode: any): Observable<Array<DomainModel>> {
    return this.httpClient.get(this.getBaseResourceEndpoint() + `/${customerCode}/accounts/${accountCode}/domains`)
      .pipe(
        map((res: Array<Domain>) => this.domainConverter.convertToModelList(res))
      );
  }

  getClient(): CustomerModel {
    return this.client || (this.storageService.getPreference('customer') ? this.customerConverter.convertCustomerFormStorage(this.storageService.getPreference('customer')) : null) || null;
  }

  getCustomerToChange(): CustomerModel {
    return this.customerToChange || (this.storageService.getPreference('customer-to-change') ? this.customerConverter.convertCustomerFormStorage(this.storageService.getPreference('customer-to-change')) : null) || null;
  }

  getAccount(): AccountModel {
    return this.account || (this.storageService.getPreference('account') ? this.accountConverter.convertAccountFormStorage(this.storageService.getPreference('account')) : null) || null;
  }

  getAccountToChange(): AccountModel {
    return this.accountToChange || (this.storageService.getPreference('account-to-change') ? this.accountConverter.convertAccountFormStorage(this.storageService.getPreference('account-to-change')) : null) || null;
  }

  setCustomer(customer: CustomerModel) {
    this.client = customer;
    this.monitoringService.addAttributeToCurrentSession(MonitoringAttributes.CUSTOMER, customer.customerCode);
    this.storageService.setPreference('customer', customer);
    this.customerChosen.next(true);
  }

  setCustomerToChange(customer: CustomerModel) {
    this.customerToChange = customer;
    this.storageService.setPreference('customer-to-change', customer);
  }

  setAccount(account: AccountModel) {
    this.account = account;
    this.storageService.setPreference('account', account);
    if(!account){
      this.accountChosen.next(false);
      return;
    }
    this.monitoringService.addAttributeToCurrentSession(MonitoringAttributes.ACCOUNT, account.accountCode);
    this.accountChosen.next(true);
  }

  setAccountToChange(account: AccountModel) {
    this.accountToChange = account;
    this.storageService.setPreference('account-to-change', account);
  }

  clearUnit(change?: boolean) {
    this.client = null;
    this.account = null;
    this.monitoringService.clearAttributeFromCurrentSession(MonitoringAttributes.CUSTOMER);
    this.monitoringService.clearAttributeFromCurrentSession(MonitoringAttributes.ACCOUNT);

    this.storageService.deletePreference('customer');
    this.storageService.deletePreference('account');
    this.storageService.setPreference('only-unit', false);
    if (change) {
      this.clearCustomerAccountToChange();
    }
    this.customerChosen.next(false);
    this.accountChosen.next(false);
  }

  clearCustomerAccountToChange() {
    this.storageService.deletePreference('customer-to-change');
    this.storageService.deletePreference('account-to-change');
  }

  changeClienteAccount() {
    if (this.isUserOnlyUnit()) {
      this.messageService.throwMessageSimple('Nessuna unit selezionabile', MessageType.WARNING_MESSAGE);
      return;
    }
    this.router.navigate(['client-account-choice']);
    this.setCustomerToChange(this.client);
    this.setAccountToChange(this.account);
    this.clearUnit();
  }

  // nel caso la unit fosse unica
  setUserOnlyUnit(flag: boolean) {
    return this.storageService.setPreference('only-unit', flag);
  }

  isUserOnlyUnit() {
    return this.storageService.getPreference('only-unit');
  }

  clearOnlyUnit() {
    this.storageService.deletePreference('only-unit');
  }

  getProductByCustomer(customerCode: string): Observable<Array<ProductState>> {
    return this.httpClient.get(this.getBaseResourceEndpoint() + `/${customerCode}/productStates`)
      .pipe(
        map((res: Array<ProductState>) => res)
      );
  }

  getProductByCustomerAccount(customerCode: string, accountCode: string): Observable<Array<ProductState>> {
    return this.httpClient.get(this.getBaseResourceEndpoint() + `/${customerCode}/accounts/${accountCode}/productStates`)
      .pipe(
        map((res: Array<ProductState>) => res)
      );
  }
}
